import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { CommitteeMemberService } from '../services/committeeMember.service';
import { DesignationService } from '../services/designation.service';
import { MediaServices } from '../services/media.service';
import { getFullImageUrl } from '../utils';
import ImageUploader from '../components/common/ImageUploader';

export default function CommitteeForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const [designations, setDesignations] = useState([]);

  const [formData, setFormData] = useState({
    member_name: '',
    member_email: '',
    mobile_number: '',
    state_union_territory: '',
    description: '',
    status: 'active',
    designations: [] // Array of designation IDs
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        // Fetch all designations
        const desigRes = await DesignationService.getAllDesignations();
        const desigList = Array.isArray(desigRes) ? desigRes : (desigRes?.data || []);
        // Only active designations should be selectable ideally, but let's show all or just active
        setDesignations(desigList.filter(d => d.status === 'active'));

        // Fetch existing member if edit mode
        if (isEditMode) {
          const memberRes = await CommitteeMemberService.getCommitteeMember(id);
          const memberData = memberRes?.data || memberRes;
          if (memberData) {
            setFormData({
              member_name: memberData.member_name || '',
              member_email: memberData.member_email || '',
              mobile_number: memberData.mobile_number || '',
              state_union_territory: memberData.state_union_territory || '',
              description: memberData.description || '',
              status: memberData.status || 'active',
              designations: Array.isArray(memberData.designations)
                ? memberData.designations.map(d => d._id || d.id || d)
                : []
            });

            if (memberData.memberImage?.basePath) {
              setImagePreview(getFullImageUrl(memberData.memberImage.name));
            }
          }
        }
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setIsFetching(false);
      }
    };

    initialize();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDesignationToggle = (desigId) => {
    setFormData(prev => ({ ...prev, designations: [desigId] }));
  };

  const handleImageCropped = (file, previewUrl) => {
    setImageFile(file);
    setImagePreview(previewUrl);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.member_name || !formData.member_email || !formData.state_union_territory) {
      toast.error('Name, Email and State/Zone are required');
      return;
    }
    if (formData.designations.length === 0) {
      toast.error('Please select at least one designation');
      return;
    }

    setIsLoading(true);
    try {
      let image_id = null;
      if (imageFile) {
        toast.loading("Uploading photo...", { id: "uploadToast" });
        const mediaRes = await MediaServices.uploadMedia(imageFile, "image", "committee");
        toast.dismiss("uploadToast");
        if (mediaRes?.success && mediaRes?.data) {
          image_id = mediaRes.data.id;
        } else {
          toast.error("Photo upload failed. Please try again.");
          setIsLoading(false);
          return;
        }
      }

      const payload = { ...formData };
      if (image_id) {
        payload.image_id = image_id;
      }
      console.log("Image id : ", image_id)
      if (isEditMode) {
        await CommitteeMemberService.updateCommitteeMember(id, payload);
        toast.success('Committee member updated successfully!');
      } else {
        await CommitteeMemberService.createCommitteeMember(payload);
        toast.success('Committee member created successfully!');
      }
      navigate('/committee');
    } catch (error) {
      toast.dismiss("uploadToast");
      toast.error(error?.response?.data?.message || 'Failed to save committee member');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return <div className="page-content"><p>Loading...</p></div>;
  }

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/committee')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '40px', height: '40px', borderRadius: '50%',
            border: '1px solid var(--border-color)', background: 'white',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={20} color="var(--text-secondary)" />
        </button>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
            {isEditMode ? 'Edit Committee Member' : 'Add Committee Member'}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Configure member details and assign multiple designations
          </p>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
        <form onSubmit={handleSubmit}>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '32px' }}>

            {/* Left Column - Image & Designations */}
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: 'var(--primary-orange)' }}>Profile Photo</h3>

              <ImageUploader
                initialImage={imagePreview}
                onImageCropped={handleImageCropped}
                onImageRemoved={removeImage}
                aspectRatio={1}
                circularCrop={false}
              />

              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: 'var(--primary-orange)', borderTop: '1px solid var(--border-color)', paddingTop: '24px' }}>
                Designation *
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
                {designations.length === 0 ? (
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>No active designations found.</p>
                ) : (
                  designations.map(desig => {
                    const desigId = desig._id || desig.id;
                    const isSelected = formData.designations.includes(desigId);
                    return (
                      <label
                        key={desigId}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
                          border: isSelected ? '1px solid var(--primary-orange)' : '1px solid var(--border-color)',
                          borderRadius: '8px', cursor: 'pointer',
                          background: isSelected ? 'var(--primary-orange-light)' : 'white',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="radio"
                          name="designationSelection"
                          checked={isSelected}
                          onChange={() => handleDesignationToggle(desigId)}
                          style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: 'var(--primary-orange)' }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: isSelected ? '600' : '400', color: isSelected ? 'var(--primary-orange)' : 'var(--text-main)' }}>
                          {desig.name}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right Column - Form Fields */}
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: 'var(--primary-orange)' }}>Personal Details</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Full Name *</label>
                  <input name="member_name" value={formData.member_name} onChange={handleChange} placeholder="e.g. John Doe" required />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" name="member_email" value={formData.member_email} onChange={handleChange} placeholder="e.g. john@example.com" required />
                </div>

                <div className="form-group">
                  <label>Mobile Number</label>
                  <input name="mobile_number" value={formData.mobile_number} onChange={handleChange} placeholder="e.g. +91 9876543210" />
                </div>

                <div className="form-group">
                  <label>State / Zone *</label>
                  <input name="state_union_territory" value={formData.state_union_territory} onChange={handleChange} placeholder="e.g. Maharashtra Zone 1" required />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Description</label>
                  <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Enter a brief biography or role description..." rows={5} />
                </div>
              </div>
            </div>

          </div>

          <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'flex-end', gap: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '24px' }}>
            <button
              type="button"
              onClick={() => navigate('/committee')}
              style={{ padding: '12px 24px', borderRadius: '10px', border: '1px solid var(--border-color)', background: '#F8F9FB', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: '600' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 32px', borderRadius: '10px', border: 'none', background: 'var(--primary-orange)', color: 'white', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: '600' }}
            >
              <Save size={18} />
              {isLoading ? 'Saving...' : 'Save Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
